import type { Result } from 'true-myth';

import type {
    NotificationPage,
    NotificationReadState,
    NotificationType,
    UnreadNotificationCount,
} from '~/shared/data-contract/notification';
import { notificationPageSchema, unreadNotificationCountSchema } from '~/shared/data-contract/notification';
import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

const DEFAULT_PAGE_SIZE = 20;

export type NotificationsFetchers = {
    readonly getUnreadNotificationCount: () => Promise<Result<UnreadNotificationCount, HttpFetcherError>>;
    readonly listNotifications: (args: {
        readonly cursor?: string;
        readonly pageSize?: number;
        readonly readState: NotificationReadState;
        readonly type?: NotificationType;
    }) => Promise<Result<NotificationPage, HttpFetcherError>>;
    readonly markNotificationRead: (args: { readonly id: string }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly markNotificationsReadBatch: (args: {
        readonly ids: readonly string[];
    }) => Promise<Result<unknown, HttpFetcherError>>;
};

export type NotificationsFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createNotificationsFetchers = (dependencies: NotificationsFetchersDependencies): NotificationsFetchers => {
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = dependencies;

    const listNotifications: NotificationsFetchers['listNotifications'] = async ({
        cursor,
        pageSize = DEFAULT_PAGE_SIZE,
        readState,
        type,
    }) => {
        return validatingCobrainerApiFetcher.get(notificationPageSchema, {
            pathname: '/notifications',
            requestOptions: {
                searchParams: {
                    pageSize,
                    readState,
                    ...(cursor === undefined ? {} : { cursor }),
                    ...(type === undefined ? {} : { type }),
                },
            },
        });
    };

    const getUnreadNotificationCount: NotificationsFetchers['getUnreadNotificationCount'] = async () => {
        return validatingCobrainerApiFetcher.get(unreadNotificationCountSchema, {
            pathname: '/notifications/unread-count',
        });
    };

    const markNotificationRead: NotificationsFetchers['markNotificationRead'] = async ({ id }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/notifications/${id}/read`,
            payload: {},
        });
    };

    const markNotificationsReadBatch: NotificationsFetchers['markNotificationsReadBatch'] = async ({ ids }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: '/notifications/read-batch',
            payload: { ids },
        });
    };

    return {
        getUnreadNotificationCount,
        listNotifications,
        markNotificationRead,
        markNotificationsReadBatch,
    };
};
